import { useContext, useMemo } from 'react';
import {
  TEDEKBokforderSTATUSEnum,
  TEDEKBokforderTYPEnum,
} from '../../../../../../globalQueryTypes';
import { IEditMenuAction } from '../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormUtils';
import {
  FormUpdateAssociatonType,
  getFormUpdateValue,
} from '../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormValueUtils';
import {
  IFranvaroUserOption,
  certifyOrRetakeRows,
  findFranvaroForUser,
  getAttestanterWithCurrentUserAsReserv,
  getRowsDeleteObjects,
  isRetakeButtonDisabled,
  isRetakeButtonHidden,
} from '../Utils/BokforderUtils';
import { FranvaroContext } from '../../../../SystemAdministration/ObjektVyer/User/Context/FranvaroContext/FranvaroContext';
import { BokforderWrapperContext } from '../../../Rutiner/Bokforder/Contexts/BokforderWrapperContext';
import { AdditionalAttestDialogOpenEnum } from '../../../Rutiner/Bokforder/Components/BokforderProviderWrapper';
import { BokforderType__Associations_Rader } from '../Queries/_QueryTypes/BokforderType';
import { UtanordningType__Associations_Rader } from '../Queries/_QueryTypes/UtanordningType';
import { mergeBusinessDataMutations } from '../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/MergeBusinessDataMutations';

export const useBokforderEditMenuRetakeButton = (
  queriesLoading: boolean,
  userID: string | null,
  setEditMessageID: React.Dispatch<React.SetStateAction<string>>
): IEditMenuAction => {
  const franvaroContext = useContext(FranvaroContext);
  const bokforderWrapperContext = useContext(BokforderWrapperContext);

  const setAdditionalAttestDialogOpen =
    bokforderWrapperContext.setAdditionalAttestDialogOpen;

  return useMemo(
    () => ({
      id: 'retakeButton',
      isDisabled: editMenuActionParams =>
        queriesLoading ||
        franvaroContext.franvaroReservForQueryLoading ||
        isRetakeButtonDisabled(
          userID ?? '',
          franvaroContext.franvaroReservForData
        )(editMenuActionParams),
      isHidden: isRetakeButtonHidden(
        userID ?? '',
        franvaroContext.franvaroReservForData
      ),
      label: 'Återta',
      icon: 'replay_outlined',
      hideIconIfNotFloating: true,
      onClick: params => {
        setEditMessageID('');
        let newValues: FormUpdateAssociatonType[][] = [];

        const rows:
          | BokforderType__Associations_Rader[]
          | UtanordningType__Associations_Rader[] =
          params.businessDataFormContext.formState.currentValues?._Associations?.Rader?.filter(
            (
              row:
                | BokforderType__Associations_Rader
                | UtanordningType__Associations_Rader
            ) => row.ATTESTERAD_DATUM != null
          ) ?? [];

        const emptyRowsValues = getRowsDeleteObjects(
          params.businessDataFormContext.formState.currentValues,
          true,
          true
        );

        if (emptyRowsValues.length > 0) {
          newValues = newValues.concat(emptyRowsValues);
        }

        const attestRows: BokforderType__Associations_Rader[] =
          rows?.filter(
            (row: any) => row?._Associations?.Attestant?.id === userID
          ) ?? [];

        const isAttestant = attestRows.length > 0;

        const haveAttesterat = attestRows.every(
          row => row.ATTESTERAD_DATUM != null
        );

        const currentAttestanterWithCurrentUserAsReserv: IFranvaroUserOption[] =
          getAttestanterWithCurrentUserAsReserv(
            params.businessDataFormContext?.formState.currentValues
              ?._Associations?.Rader ?? [],
            franvaroContext.franvaroReservForData,
            params.businessDataFormContext?.data?.TYP
          );

        const attesteradeAttestanter =
          currentAttestanterWithCurrentUserAsReserv.filter(
            user => user.Attesterat && user.AttesteratAsCurrentReserv
          );

        const isKontrollattestant =
          params.businessDataFormContext.data?._Associations?.Kontrollattestant
            ?.id === userID;

        const today = new Date();

        const kontrollattestantWithCurrentUserAsReserv =
          franvaroContext.franvaroReservForData.find(franvaro =>
            findFranvaroForUser(
              params.businessDataFormContext.formState.currentValues
                ?._Associations?.Kontrollattestant?.id,
              TEDEKBokforderTYPEnum.UTANORDNING,
              today,
              franvaro
            )
          );

        const isReservForKontrollattestant =
          kontrollattestantWithCurrentUserAsReserv?._Associations?.Reserv?.id !=
            null &&
          kontrollattestantWithCurrentUserAsReserv?._Associations?.Reserv
            ?.id ===
            params.businessDataFormContext.data?._Associations
              ?.ReservKontrollattestant?.id;

        const isKontrollattesterad =
          params.businessDataFormContext.data?.KONTROLLATTESTERAD_DATUM != null;

        if (
          ((attesteradeAttestanter.length > 0 &&
            isAttestant &&
            haveAttesterat) ||
            attesteradeAttestanter.length > 1) &&
          !isKontrollattesterad
        ) {
          setAdditionalAttestDialogOpen(AdditionalAttestDialogOpenEnum.ATERTA);
        } else {
          const isBestallare =
            params.businessDataFormContext.data?._Associations?.Bestallare
              ?.id === userID;

          const status =
            (isAttestant && haveAttesterat) ||
            (isKontrollattestant && isKontrollattesterad) ||
            (isReservForKontrollattestant && isKontrollattesterad) ||
            attesteradeAttestanter.length > 0
              ? TEDEKBokforderSTATUSEnum.VANTAR_PA_ATTEST
              : isBestallare
              ? TEDEKBokforderSTATUSEnum.UTKAST
              : TEDEKBokforderSTATUSEnum.UTKAST;

          newValues.push([
            {
              operation: 'OwnFields',
              values: {
                STATUS: status,
                STATUS_DATUM: new Date().toLocaleDateString('sv-SE'),
              },
            },
          ]);

          if (isAttestant && haveAttesterat && !isKontrollattesterad) {
            const updatedRows = certifyOrRetakeRows(
              rows ?? [],
              userID ?? '',
              ''
            );

            newValues = newValues.concat(updatedRows);
          } else if (
            attesteradeAttestanter.length === 1 &&
            !isKontrollattesterad
          ) {
            const currentAttestantWithMeAsReserv = attesteradeAttestanter[0];

            const reservRows = certifyOrRetakeRows(
              rows ?? [],
              currentAttestantWithMeAsReserv.id ?? '',
              '',
              userID
            );

            newValues = newValues.concat(reservRows);
          }

          if (
            (isKontrollattestant || isReservForKontrollattestant) &&
            isKontrollattesterad
          ) {
            const kontrollUpdateValues: FormUpdateAssociatonType[] = [
              {
                operation: 'OwnFields',
                values: {
                  KONTROLLATTESTERAD_DATUM: null,
                },
              },
            ];

            if (
              params.businessDataFormContext.data?._Associations
                ?.ReservKontrollattestant != null
            ) {
              const reservForKontrollattestantUpdateValues: FormUpdateAssociatonType =
                {
                  associationName: 'ReservKontrollattestant',
                  operation: 'Ref',
                  id: null,
                };
              kontrollUpdateValues.push(reservForKontrollattestantUpdateValues);
            }

            newValues.push(kontrollUpdateValues);
          }

          const mergedValues = mergeBusinessDataMutations(
            getFormUpdateValue(
              newValues,
              params.businessDataFormContext.data ?? {}
            ),
            params.businessDataFormContext.formState.values ?? {}
          );

          params.businessDataFormContext.saveForm(mergedValues);
        }
      },
    }),
    [
      franvaroContext.franvaroReservForData,
      franvaroContext.franvaroReservForQueryLoading,
      queriesLoading,
      setEditMessageID,
      setAdditionalAttestDialogOpen,
      userID,
    ]
  );
};
